import {
  InternalUserGroup, Permission, type UserSession 
} from '@/types/auth';

const GROUP_PERMISSIONS: Record<string, Permission[]> = {
  [InternalUserGroup.ADMIN]: Object.values(Permission),
  
  [InternalUserGroup.FINANCE]: [
    Permission.VIEW_CATALOG,
    Permission.VIEW_RECIPES,
    Permission.VIEW_INVENTORY,
    Permission.VIEW_RECEIVING,
    Permission.APPROVE_POS,
    Permission.VIEW_POS,
    Permission.VIEW_SUPPLIERS,
    Permission.MANAGE_INVOICES,
    Permission.VIEW_INVOICES,
    Permission.VIEW_SPEND,
    Permission.VIEW_ANALYTICS,
    Permission.EXPORT_REPORTS,
    Permission.VIEW_WASTE,
  ],
  
  [InternalUserGroup.BUYER]: [
    Permission.MANAGE_CATALOG,
    Permission.VIEW_CATALOG,
    Permission.VIEW_RECIPES,
    Permission.VIEW_INVENTORY,
    Permission.VIEW_RECEIVING,
    Permission.MANAGE_SOURCING,
    Permission.APPROVE_POS,
    Permission.VIEW_POS,
    Permission.MANAGE_SUPPLIERS,
    Permission.VIEW_SUPPLIERS,
    Permission.VIEW_INVOICES,
    Permission.VIEW_SPEND,
    Permission.VIEW_ANALYTICS,
    Permission.EXPORT_REPORTS,
  ],
  
  [InternalUserGroup.SUPERVISOR]: [
    Permission.VIEW_USERS,
    Permission.VIEW_CATALOG,
    Permission.MANAGE_RECIPES,
    Permission.VIEW_RECIPES,
    Permission.MANAGE_MENUS,
    Permission.VIEW_MENUS,
    Permission.MANAGE_INVENTORY,
    Permission.VIEW_INVENTORY,
    Permission.TRANSFER_INVENTORY,
    Permission.APPROVE_REQUISITIONS,
    Permission.CREATE_REQUISITIONS,
    Permission.VIEW_REQUISITIONS,
    Permission.RECEIVE_GOODS,
    Permission.VIEW_RECEIVING,
    Permission.VIEW_POS,
    Permission.VIEW_SUPPLIERS,
    Permission.VIEW_SPEND,
    Permission.VIEW_ANALYTICS,
    Permission.EXPORT_REPORTS,
    Permission.LOG_WASTE,
    Permission.VIEW_WASTE,
    Permission.MANAGE_PRODUCTION,
    Permission.VIEW_PRODUCTION,
  ],
  
  [InternalUserGroup.SITE_MANAGER]: [
    Permission.VIEW_CATALOG,
    Permission.VIEW_RECIPES,
    Permission.VIEW_MENUS,
    Permission.MANAGE_INVENTORY,
    Permission.VIEW_INVENTORY,
    Permission.TRANSFER_INVENTORY,
    Permission.APPROVE_REQUISITIONS,
    Permission.CREATE_REQUISITIONS,
    Permission.VIEW_REQUISITIONS,
    Permission.RECEIVE_GOODS,
    Permission.VIEW_RECEIVING,
    Permission.VIEW_POS,
    Permission.LOG_WASTE,
    Permission.VIEW_WASTE,
    Permission.MANAGE_PRODUCTION,
    Permission.VIEW_PRODUCTION,
  ],
  
  [InternalUserGroup.STOREKEEPER]: [
    Permission.VIEW_CATALOG,
    Permission.VIEW_RECIPES,
    Permission.MANAGE_INVENTORY,
    Permission.VIEW_INVENTORY,
    Permission.TRANSFER_INVENTORY,
    Permission.VIEW_REQUISITIONS,
    Permission.VIEW_RECEIVING,
    Permission.LOG_WASTE,
    Permission.VIEW_PRODUCTION,
  ],
  
  [InternalUserGroup.RECEIVING]: [
    Permission.VIEW_CATALOG,
    Permission.VIEW_INVENTORY,
    Permission.RECEIVE_GOODS,
    Permission.VIEW_RECEIVING,
    Permission.VIEW_POS,
  ],
  
  [InternalUserGroup.CHEF]: [
    Permission.VIEW_CATALOG,
    Permission.MANAGE_RECIPES,
    Permission.VIEW_RECIPES,
    Permission.MANAGE_MENUS,
    Permission.VIEW_MENUS,
    Permission.VIEW_INVENTORY,
    Permission.CREATE_REQUISITIONS,
    Permission.VIEW_REQUISITIONS,
    Permission.LOG_WASTE,
    Permission.VIEW_WASTE,
    Permission.MANAGE_PRODUCTION,
    Permission.VIEW_PRODUCTION,
  ],
  
  [InternalUserGroup.VENUE_STAFF]: [
    Permission.VIEW_CATALOG,
    Permission.VIEW_RECIPES,
    Permission.VIEW_MENUS,
    Permission.VIEW_INVENTORY,
    Permission.CREATE_REQUISITIONS,
    Permission.VIEW_REQUISITIONS,
    Permission.LOG_WASTE,
    Permission.VIEW_PRODUCTION,
  ],
};

export function hasPermission(user: UserSession, permission: Permission): boolean {
  return getUserPermissions(user).includes(permission);
}

export function hasAnyPermission(user: UserSession, permissions: Permission[]): boolean {
  return permissions.some((p) => hasPermission(user, p));
}

export function getUserPermissions(user: UserSession): Permission[] {
  const permissions = new Set<Permission>();
  for (const group of user.groups) {
    const groupPerms = GROUP_PERMISSIONS[group];
    if (groupPerms) {
      groupPerms.forEach((p) => permissions.add(p));
    }
  }
  return Array.from(permissions);
}

export function canAccessSite(user: UserSession, siteId: string): boolean {
  if (user.siteAccess.length === 0) return true;
  return user.siteAccess.includes(siteId);
}

export function getPrimaryGroup(user: UserSession): string {
  const priority = Object.values(InternalUserGroup);
  for (const group of priority) {
    if (user.groups.includes(group)) return group;
  }
  return user.groups[0] ?? 'unknown';
}
